import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Upload, X, Image, Video, File } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// File upload interface
export interface FileUpload {
  url: string | File // string for existing files, File for new uploads
  fileName: string
  type: 'image' | 'video'
  size?: number
}

interface MultipleMediaUploadProps {
  uploadedFiles: FileUpload[]
  onFilesChange: (files: FileUpload[]) => void
  onFileDelete?: (deletedUrl: string) => void
  columns?: number
  maxFiles?: number
  maxSize?: number // in MB
  acceptedTypes?: string[]
  title?: string
  className?: string
}

const DEFAULT_ACCEPTED_TYPES: string[] = ['image/*', 'video/*']

export function MultipleMediaUpload({
  uploadedFiles,
  onFilesChange,
  onFileDelete,
  columns = 1,
  maxFiles = 5,
  maxSize = 50, // 50MB
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  title = 'รูปภาพและวิดีโอ',
  className = '',
}: MultipleMediaUploadProps) {
  const [error, setError] = useState<string>('')

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `ไฟล์ ${file.name} มีขนาดใหญ่เกิน ${maxSize}MB`
    }

    // Check file type
    const isValidType = acceptedTypes.some((type) => {
      if (type.endsWith('/*')) {
        const baseType = type.replace('/*', '')
        return file.type.startsWith(baseType)
      }
      return file.type === type
    })

    if (!isValidType) {
      return `ไฟล์ ${file.name} ไม่ใช่ประเภทที่รองรับ`
    }

    return null
  }

  const processFiles = useCallback(
    (fileList: FileList) => {
      const newFiles: FileUpload[] = []
      const errors: string[] = []

      Array.from(fileList).forEach((file) => {
        const error = validateFile(file)
        if (error) {
          errors.push(error)
          return
        }

        const fileType = file.type.startsWith('image/') ? 'image' : 'video'

        const fileItem: FileUpload = {
          url: file,
          fileName: file.name,
          type: fileType,
          size: file.size,
        }

        newFiles.push(fileItem)
      })

      if (errors.length > 0) {
        setError(errors.join(', '))
        setTimeout(() => setError(''), 5000)
        return
      }

      if (uploadedFiles.length + newFiles.length > maxFiles) {
        setError(`สามารถอัพโหลดได้สูงสุด ${maxFiles} ไฟล์`)
        setTimeout(() => setError(''), 5000)
        return
      }

      const updatedFiles = [...uploadedFiles, ...newFiles]
      onFilesChange(updatedFiles)
      setError('')
    },
    [uploadedFiles, maxFiles, acceptedTypes, maxSize, onFilesChange],
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFiles(e.target.files)
      }
    },
    [processFiles],
  )

  const removeFile = useCallback(
    async (index: number) => {
      const fileToRemove = uploadedFiles[index]

      if (fileToRemove && typeof fileToRemove.url === 'string') {
        // Do NOT delete from storage here. Only notify parent to mark for deletion.
        if (onFileDelete) {
          onFileDelete(fileToRemove.url)
        }
      }

      const updatedFiles = uploadedFiles.filter((_, i) => i !== index)
      onFilesChange(updatedFiles)
    },
    [uploadedFiles, onFilesChange, onFileDelete],
  )

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (type: 'image' | 'video') => {
    switch (type) {
      case 'image':
        return <Image className="h-4 w-4" />
      case 'video':
        return <Video className="h-4 w-4" />
      default:
        return <File className="h-4 w-4" />
    }
  }

  const getFilePreviewUrl = (file: FileUpload): string => {
    if (typeof file.url === 'string') {
      return file.url
    } else {
      return URL.createObjectURL(file.url)
    }
  }

  const getFileType = (file: FileUpload): 'image' | 'video' => {
    if (file.type) return file.type

    if (typeof file.url === 'string') {
      // Try to determine from URL extension
      const extension = file.url.split('.').pop()?.toLowerCase()
      if (['mp4', 'avi', 'mov', 'wmv'].includes(extension || '')) {
        return 'video'
      }
      return 'image'
    } else {
      return file.url.type.startsWith('image/') ? 'image' : 'video'
    }
  }

  const getFileSize = (file: FileUpload): number => {
    if (file.size) return file.size

    if (typeof file.url === 'string') {
      return 0 // Unknown size for existing files
    } else {
      return file.url.size
    }
  }

  return (
    <Card className={`border rounded-lg ${className}`}>
      <CardHeader>
        <CardTitle className="text-base font-semibold text-black">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                document.getElementById('multiple-media-file-input')?.click()
              }
            >
              <Upload className="h-4 w-4 mr-2" />
              อัปโหลดไฟล์
            </Button>
          </div>

          <input
            id="multiple-media-file-input"
            type="file"
            multiple
            accept={acceptedTypes.join(',')}
            onChange={handleFileInput}
            className="hidden"
          />

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* File List */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900">
                ไฟล์ที่เลือก ({uploadedFiles.length}/{maxFiles})
              </h4>
              <div
                className="grid gap-3"
                style={{
                  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                }}
              >
                {uploadedFiles.map((file, index) => {
                  const fileType = getFileType(file)
                  const fileSize = getFileSize(file)
                  const previewUrl = getFilePreviewUrl(file)

                  return (
                    <div
                      key={index}
                      className="relative group border rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition-colors min-w-[190px]"
                    >
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>

                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {fileType === 'image' ? (
                            <img
                              src={previewUrl}
                              alt={file.fileName}
                              className="h-12 w-12 object-cover rounded"
                            />
                          ) : (
                            <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center">
                              {getFileIcon(fileType)}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {file.fileName}
                          </p>
                          {fileSize > 0 && (
                            <p className="text-xs text-gray-500">
                              {formatFileSize(fileSize)}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 capitalize">
                            {fileType}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
